import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { BookingListModel } from '../../../../models/booking.model';
import { BookingService } from '../../../../services/booking.service';
import { getStatus, getStatusString } from '../../../../utils/booking.utils';
import { getUserFullName } from '../../../../utils/user.utils';
import {
  Filter,
  TableColumn,
} from '../../../shared-components/custom-table/custom-table.component';
import moment from 'moment';

@Component({
  selector: 'app-bookings',
  templateUrl: './bookings.component.html',
  styleUrl: './bookings.component.scss',
  standalone: false,
})
export class BookingsComponent implements OnInit {
  public dataSource$: Observable<BookingListModel[]>;
  public isLoading = true;
  public bookings: BookingListModel[] = [];
  public selectedYear: number = moment().year();
  public yearOptions: { label: string; value: number }[] = [];

  constructor(
    private bookingService: BookingService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    // Generate year options (current year and 5 years back)
    const currentYear = moment().year();
    for (let i = 0; i <= 5; i++) {
      const year = currentYear - i;
      this.yearOptions.push({
        label: year.toString(),
        value: year,
      });
    }
  }

  public ngOnInit(): void {
    this.getBookings();
  }

  private getBookings(): void {
    this.bookingService
      .getAllBookings()
      .pipe(
        map(bookings => {
          this.bookings = bookings;
          this.dataSource$ = of(bookings);
        }),
        catchError(error => {
          console.error('Error fetching users', error);
          return of([]);
        })
      )
      .subscribe({
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  public columns: TableColumn[] = [
    {
      columnDef: 'user',
      header: 'Ersteller',
      width: '20%',
      filterValue: (row: BookingListModel) => getUserFullName(row.user),
      avatarUser: row => row.user,
      sortable: true,
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'bookingNr',
      header: 'Buchungs ID',
      width: '15%',
      fontWeight: '400',
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'seminarName',
      header: 'Seminar',
      width: '20%',
      fontWeight: '400',
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'attendeeCount',
      header: 'Personen',
      width: '10%',
      fontWeight: '400',
    },
    {
      columnDef: 'status',
      header: 'Status',
      width: '10%',
      filterValue: row => getStatusString(row),
      render: row => {
        const { status, class: statusClass } = getStatus(row);

        return `<div class="${statusClass}">
        ${status}
      </div>`;
      },
    },
    {
      columnDef: 'actions',
      header: 'Aktionen',
      headerAlign: 'center',
      cellAlign: 'center',
      width: '10%',
      fontWeight: '400',
      render: () =>
        `<button class="table-action-button"><i class="fa-regular fa-chevron-down"></i></button>`,
      onCellClick: this.onCellClick.bind(this),
    },
  ];

  public onCellClick(row: any, column: any) {
    this.router.navigate([btoa(String(row.id))], {
      relativeTo: this.activatedRoute,
    });
  }

  public exportBookingsExcel() {
    this.bookingService.exportBookingsExcel(this.selectedYear).subscribe({
      next: () => {
        // File download handled in service
      },
      error: error => {
        console.error('Error exporting bookings:', error);
      },
    });
  }

  public exportBookingsExcelPerAttendee() {
    this.bookingService
      .exportBookingsExcelPerAttendee(this.selectedYear)
      .subscribe({
        next: () => {
          // File download handled in service
        },
        error: error => {
          console.error('Error exporting bookings per attendee:', error);
        },
      });
  }

  public filters: Filter[] = [
    {
      filteredColumn: 'status',
      type: 'multiselect',
      selectOptions: [
        {
          value: 'confirmed',
          label: 'Bezahlt',
        },
        {
          value: 'late',
          label: 'Verspätet',
        },
        {
          value: 'waitlist',
          label: 'Warteliste',
        },
        {
          value: 'pending',
          label: 'Ausstehend',
        },
        {
          value: 'cancelled',
          label: 'Storniert',
        },
      ],
      placeholder: 'Status auswählen...',
      label: 'Status',
    },
  ];
}
